/**
 * THE ONE FILE MS4 REPLACED.
 *
 * Every surface in this app reads identity, current organization and role from
 * the session contract in `query/query-options.ts`. This module is what puts a
 * value behind that contract. MS4 T4.6 (OPSSTD-4691) swapped the body of
 * `resolveSession` for the real read against the Sync Server's session
 * endpoint, which itself sits in front of Keycloak; the contract, the
 * guards and every surface stayed exactly as they were.
 *
 * That substitution only worked while nothing else imported this file. The
 * rule is in `.claude/rules/routing.md` — guards read the session through
 * `context.queryClient`, `beforeLoad` decides without returning data, and no
 * route context carries a session-derived value.
 *
 * WHAT THE CONTRACT CARRIES, AND WHAT IT MUST NEVER CARRY. Identity, current
 * organization, role — those three values, and nothing else. Nothing
 * the identity provider issues belongs here: no bearer credential of any kind
 * and no raw assertion out of the token. All of that stays server-side
 * (T4.1.3); the browser only ever holds an `HttpOnly` session cookie it can't
 * read, plus the `csrfToken` this resolver hands back.
 */
import { v1AuthGetSession, v1UsersGetMe } from '@repo/sdk/gen'

/**
 * The product's three roles. A union, never a free string: `/admin/*` and
 * `/operator/*` branch on it, and a typo in a string literal would read as
 * "not authorized" — a guard that silently denies is worse than one that fails
 * to compile.
 *
 * `platform-operator` is unreachable from this resolver — that role's session
 * lives entirely in the separate `backoffice/auth` identity plane the operator
 * artifact reads from, never from this app's `get-session`. It stays in the
 * union because the guards and navigation this type feeds were written once
 * against all three and the operator's separate artifact is what makes
 * narrowing them safe, not a change to this file.
 */
export type AppRole = 'platform-operator' | 'organization-admin' | 'end-user'

/**
 * `org_admin` is the only backend role that grants anything beyond the
 * end-user surface — `/admin/*` is an increase in surface on the same
 * session, not a different identity (per the 06/08 decision recorded in
 * `query/query-options.ts`). Every other value, including a session with no
 * membership row at all (`role: null` — see `auth/get-session`'s response),
 * lands on `end-user`: the role with the least surface, so a state this
 * resolver has never observed in practice still renders something instead of
 * refusing everything.
 */
function toAppRole(role: 'org_admin' | 'member' | null): AppRole {
	return role === 'org_admin' ? 'organization-admin' : 'end-user'
}

/**
 * The identity the contract carries. Named because it is a cross-boundary
 * contract rather than an implementation detail: the shell renders from it.
 * `image` is nullable — `get-me`'s `image` is the richer asset shape
 * (blurhash, variants, dimensions); the contract only ever needs the URL.
 *
 * `personalFolderId` is here for the same reason the profile fields are: the
 * file manager cannot draw anything until it knows which folder is the
 * person's own, and this is the read that already answers it. It is a node
 * identifier and nothing more — no role, no name, nothing that goes stale
 * behind the `staleTime: Infinity` this contract is cached under. The name
 * would: the person can rename the folder from the moment they see it, so
 * whoever needs it reads it from the directory rather than from here.
 */
interface SessionUser {
	id: string
	name: string
	email: string
	image: { url: string } | null
	personalFolderId: string | null
}

/**
 * Resolves the current session. The return value IS the contract: an
 * authenticated arm that always carries an organization and a role, or an
 * anonymous arm that carries nothing at all.
 *
 * Two calls, not one: `get-session` alone carries only `{ id, isAnonymous }`
 * for identity (deliberately auth-only, `auth/resources/auth-identity.resource.ts`)
 * plus the real `organization`/`role`, and `get-me` is the one endpoint that
 * knows the profile fields (`name`, `email`, `image`) and the personal folder
 * this contract also needs. Both are cheap, session-cookie-authenticated reads and only ever run
 * once per session lifetime (`staleTime: Infinity` on the query wrapping this
 * function) — worth two round trips at bootstrap rather than growing
 * `get-session` into a second source of profile truth alongside `get-me`.
 */
export async function resolveSession() {
	const session = await v1AuthGetSession({})

	if (session.sessionType !== 'authenticated' || !session.organization) {
		return { sessionType: 'anonymous' as const }
	}

	// `X-CSRF-Token` explicitly, not left to the browser SDK's request
	// interceptor: that interceptor reads the token out of the session query's
	// own cache entry, which does not exist yet — this call runs *inside* the
	// queryFn that is about to populate it. `session.csrfToken`, fresh from the
	// call above, is the same value the interceptor would have read once the
	// cache existed.
	const { data: profile } = await v1UsersGetMe({
		headers: { 'X-CSRF-Token': session.csrfToken }
	})

	const user: SessionUser = {
		id: profile.id,
		name: profile.name,
		email: profile.email ?? '',
		image: profile.image ? { url: profile.image.url } : null,
		personalFolderId: profile.personalFolderId
	}

	return {
		sessionType: 'authenticated' as const,
		user,
		organization: session.organization,
		role: toAppRole(session.role),
		csrfToken: session.csrfToken
	}
}
